import { Component, OnInit, OnDestroy, AfterViewChecked, ChangeDetectorRef, Input, ViewChild, ElementRef } from '@angular/core';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { MessageService } from '../../core/service/message.service';
import { NGXLogger } from 'ngx-logger';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Message } from '../../case/model/message';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { TranslateService } from '@ngx-translate/core';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { MessageListService } from 'src/app/core/service/grid-data/grid-data-message.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UntypedFormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { FhirService } from 'src/app/core/service/fhir.service';

@Component({
  standalone: false,
  selector: 'app-message-list',
  templateUrl: './message-list.component.html',
  styleUrls: ['./message-list.component.scss']
})
export class MessageListComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() public dashboard: boolean;
  @ViewChild('splitter', { read: ElementRef }) public splitter: ElementRef;

  public pageSizes = [5, 10, 25, 50, 100];
  public enableAdvanced = false;
  public breadcrumb = '';
  public selectedMessages: number[] = [];
  public selected: Message = null;
  public messageDialogOpened = false;
  public filterText = '';
  public confirmEraseDialogOpened = false;
  public patientLabel = {};
  /* PR: Disabling hard delete (allowHardDelete=false) because we lose traceability.
         If we want to keep delete, we need a to implement a second level of soft delete.*/
  public allowHardDelete = false;
  public savingMessage = false;
  public isMemberAndCaseLinkEnabled = false;
  public fhirEmbedded = false;
  public fhirFullSite = false;
  public state: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    }
  };
  public messages = [];
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public searchControl = new UntypedFormControl();

  /**
   * Subscription to Angular component route parameters.
   */
  private routeParamSubscription: Subscription;
  private messageCountSubscription: Subscription;
  private onText = '';

  constructor(
    private authService: AuthenticationService,
    private messageService: MessageService,
    private messageListService: MessageListService,
    private route: ActivatedRoute,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private searchHelperService: SearchHelperService,
    private settingService: SettingService,
    private fhirService: FhirService,
    public intl: IntlService) { }

  public get loading(): boolean
  {
    return this.messageListService.loading;
  }

  public ngOnInit(): void
  {
    this.fhirEmbedded = this.fhirService.embedded;
    this.fhirFullSite = this.fhirService.fullSite;
    this.isMemberAndCaseLinkEnabled = this.authService.currentUser.role !== 'ROLE_REFERRAL_COORDINATOR' &&
      this.authService.currentUser.role !== 'ROLE_DIRECTORY_MANAGER' &&
      this.authService.currentUser.role !== 'ROLE_DATA_ANALYST';

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.translate.get('ON').subscribe({ next: (value) => { this.onText = value; } });

    this.state = {
      filter: {
        filters: [], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'sentOn', dir: 'desc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };

    this.routeParamSubscription = this.route.params.subscribe({
      next: (params) =>
      {
        if (params.filter === 'inbox' || this.dashboard)
        {
          this.breadcrumb = 'INBOX';
        }
        else if (params.filter === 'deleted' || params.filter === 'sent')
        {
          this.breadcrumb = params.filter.toUpperCase();
        }
        else
        {
          this.breadcrumb = 'ALL';
        }
        this.getMessages(params.filter, true);
      }
    });

    this.messageCountSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'wsMessagesCount' && this.breadcrumb === 'INBOX')
        {
          this.getMessages(this.breadcrumb);
        }
      }
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(500))
      .subscribe({
        next: (searchValue: string) =>
        {
          this.searchFieldChange(searchValue);
        }
      });
  }

  public ngAfterViewChecked(): void
  {
    // explicit change detection to avoid "expression-has-changed-after-it-was-checked-error"
    this.changeDetectorRef.detectChanges();
    this.updateSplitterBar();
  }

  public ngOnDestroy(): void
  {
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.messageCountSubscription !== undefined) { this.messageCountSubscription.unsubscribe(); }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.getMessages(this.breadcrumb);
  }

  /**
   * Updates the selected message.
   */
  public onSelectedKeysChange(event: any): void
  {
    this.selectedMessages = event;
    if (this.selectedMessages.length === 1)
    {
      this.selected = this.gridView.data[event[0] % this.state.take];

      // For SENT, allow selection but don't update read.
      if (this.breadcrumb !== 'SENT')
      {
        if (this.gridView.data[event[0] % this.state.take] !== undefined &&
          this.gridView.data[event[0] % this.state.take].status !== 'Read')
        {
          this.gridView.data[event[0] % this.state.take].status = 'Read';
          const messages = [];
          messages.push(this.gridView.data[event[0] % this.state.take]);
          this.messageService.updateMessages(messages).subscribe({
            error: (error) =>
            {
              this.logger.error(error);
              this.messageHandlerService.displayError(error);
            }
          });
        }
      }
    }
    else
    {
      this.messageDialogOpened = false;
      this.selected = null;
    }
  }

  /*
  * Open new message dialog
  */
  public newMessage(): void
  {
    this.savingMessage = false;
    this.messageDialogOpened = true;
  }

  public closeNewMessage(): void
  {
    this.messageDialogOpened = false;
    this.selectedMessages = [];
    this.selected = null;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.getMessages(this.breadcrumb, true);
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, 'subject');
    this.filterText = filterText;
    this.filterChange(root);
  }

  public deleteMessages(): void
  {
    if (this.selectedMessages.length > 0)
    {
      const messages = [];
      this.selectedMessages.forEach(index =>
      {
        messages.push(this.gridView.data[index % this.state.take]);
      });

      this.messageService.deleteMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('DELETE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public unreadMessages(): void
  {
    if (this.selectedMessages.length > 0)
    {
      const messages = [];
      this.selectedMessages.forEach(index =>
      {
        const messageToUnread: Message = this.gridView.data[index % this.state.take];
        messageToUnread.status = 'Unread';
        messages.push(this.gridView.data[index % this.state.take]);
      });

      this.messageService.updateMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('UPDATE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public readMessages(): void
  {
    if (this.selectedMessages.length > 0)
    {
      const messages = [];
      this.selectedMessages.forEach(index =>
      {
        const messageToRead: Message = this.gridView.data[index % this.state.take];
        messageToRead.status = 'Read';
        messages.push(this.gridView.data[index % this.state.take]);
      });

      this.messageService.updateMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('UPDATE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public undeleteMessages(): void
  {
    if (this.selectedMessages.length > 0)
    {
      const messages = [];
      this.selectedMessages.forEach(index =>
      {
        messages.push(this.gridView.data[index % this.state.take]);
      });

      this.messageService.undeleteMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('UPDATE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public eraseMessages(): void
  {
    if (this.selectedMessages.length > 0)
    {
      const messages = [];
      this.selectedMessages.forEach(index =>
      {
        messages.push(this.gridView.data[index % this.state.take]);
      });

      this.messageService.eraseMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('DELETE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public pageChange(): void
  {
    this.selectedMessages = [];
    this.selected = null;
  }

  /**
   * When messages in embedded and have a patient context, new message will use that member id for the compose.
   *
   * @returns Numeric Channels memberId or null.
   */
  public getNewMessageMemberId(): number
  {
    return (this.fhirService.embedded && !this.fhirService.fullSite && this.fhirService.patientContext) ? this.fhirService.patientContext?.id : undefined;
  }

  public updateSplitterBar()
  {
    const bar = this.splitter.nativeElement.querySelector('.k-splitbar');

    if (bar)
    {
      const pane = this.splitter.nativeElement.querySelector('.k-pane');
      const size = pane ? pane.offsetHeight : 0;

      bar.setAttribute('aria-valuenow', size.toString());
      bar.setAttribute('aria-valuemax', '9999');
    }
  }

  private getMessages(filter: string, resetSelection = false): void
  {
    // Optionally reset selection
    if (resetSelection)
    {
      this.selectedMessages = [];
      this.selected = null;
      this.state.skip = 0;
    }

    this.messageListService.queryByState(this.state, (messages: any) =>
    {
      if (messages !== null)
      {
        messages.data.forEach(d =>
        {
          d.sentOn = new Date(d.sentOn);
          d.toAddresses = d.to.map(x => x.name).join();
          d.from = d.fromDirectAddress ? d.fromDirectAddress : d.from;
          if ((d.body !== null) && (d.body.indexOf('\n\n\n' + this.onText) > -1))
          {
            d.body = d.body.substring(0, d.body.indexOf('\n\n\n' + this.onText));
          }
        });
        this.messages = messages.data;
        this.gridView = {
          data: (messages) ? this.gridData = messages.data : this.gridData = [],
          total: (messages) ? this.total = messages.total : this.total = 0
        };
      }
    }, `&status=${filter}`);
  }
}
